import { useCallback, useContext } from 'react'

import { AppStateContext } from '../../../state/AppProvider'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { Checkbox, FormControl, FormHelperText } from '@mui/material'
import { first, includes, intersection, isEmpty } from 'lodash'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'

import styles from './MultiSelectFormField.module.css'

// Convert semicolon-separated string to array for UI
const stringToArray = (value: string | null | undefined): string[] => {
  if (!value) return []
  return value
    .split(';')
    .map(v => v.trim())
    .filter(v => v.length > 0)
}

// Convert array to semicolon-separated string for API
const arrayToString = (values: string[]): string => {
  return values.filter(v => v.trim().length > 0).join(';')
}

interface Props {
  id?: string
  name: string
  stepName: string
  fieldName: string
  convId: string
  gridSize: number
  data: FormField<string>
  url?: string
  tooltipInfo?: React.ReactNode
  disabled?: boolean
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const MultiSelectFormField = ({
  name,
  stepName,
  fieldName,
  convId,
  gridSize,
  data,
  url,
  tooltipInfo,
  disabled = false,
  onError,
}: Props) => {
  const appStateContext = useContext(AppStateContext)

  const currentValues = stringToArray(data?.field_value)

  const selectedValuesSet = new Set(currentValues)

  const handleChange = useCallback(
    async (event: SelectChangeEvent<string[]>) => {
      try {
        const selectedArray = event.target.value as string[]
        const filteredSelectedArray = intersection(selectedArray, data.options)
        const newValue = arrayToString(filteredSelectedArray)

        const newConversation = await updateExperimentFormField(
          stepName,
          fieldName,
          newValue,
          convId,
          appStateContext?.state.shouldValidateForm ?? false,
          appStateContext?.state.frontendSettings || undefined,
        )

        if (newConversation) {
          const messages = appStateContext?.state.currentChat?.messages || []
          newConversation.messages = messages

          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
        }
      } catch (error) {
        const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
        onError?.({
          title: 'Error',
          description:
            errorWithReactElement?.reactElement ||
            (error instanceof Error ? error.message : 'An error occurred while updating the field'),
          type: 'error',
        })
      }
    },
    [stepName, fieldName, convId, appStateContext, onError],
  )

  if (data?.hidden === true) {
    return <></>
  }

  // Check for unavailable values (values not in current options)
  const unavailableValues = currentValues.filter(value => !includes(data.options, value) && !isEmpty(value))

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <FormFieldLabel
        text={name}
        isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
        linkTitle="Download Template"
        underlineLink={true}
        url={url}
        tooltipInfo={tooltipInfo}
      />

      <FormControl
        fullWidth
        error={!isEmpty(data.errors)}>
        {currentValues.length === 0 && <InputLabel shrink={false}>Select</InputLabel>}
        <Select
          multiple
          disabled={disabled}
          classes={{
            error: isEmpty(data.errors) ? undefined : styles.errorInput,
            disabled: styles.disabledSelectField,
          }}
          value={currentValues}
          onChange={handleChange}
          renderValue={selected => (
            <Box className={styles.selectedValues}>
              {selected.map((value, index) => (
                <span key={value}>
                  {value}
                  {index < selected.length - 1 && ', '}
                </span>
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              className: styles.menuPaper,
            },
          }}>
          {unavailableValues.map(value => (
            <MenuItem
              key={`unavailable-${value}`}
              value={value}
              className={styles.menuItem}
              disabled>
              <Checkbox
                checked
                disabled
              />
              {value} (unavailable)
            </MenuItem>
          ))}
          {data.options?.map(option => (
            <MenuItem
              key={option}
              value={option}
              className={styles.menuItem}
              classes={{
                selected: styles.menuItemSelected,
              }}>
              <Checkbox
                checked={selectedValuesSet.has(option)}
                classes={{ checked: styles.menuItemSelectedCheckbox }}
              />
              {option}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{first(data.errors)}</FormHelperText>
      </FormControl>
    </Grid>
  )
}
